/**
 * notificationMappers
 * -------------------
 * Single source of truth that turns raw backend objects (orders, waiter table
 * tasks) into a normalized notification shape used everywhere notifications are
 * shown: the navbar dynamic island (RestaurantNotificationCenter) and the
 * NewNotificationDrawer.
 *
 * WHY this exists: the island and the drawer used to each map data their own
 * way, which drifted (duplicate logic, mismatched labels/counts). Centralizing
 * here guarantees both show the exact same notifications, counts and read-ids.
 *
 * Normalized notification shape:
 *   { id, type, title, description, tags[], createdAt, filterType }
 *   - id:        stable, status-aware key (`${orderId}_${status}`) — see below.
 *   - type:      drives icon/colour (NEW_ORDER, ORDER_READY, PAYMENT_RECEIVED…).
 *   - tags[0]:   the "source" (Website/WhatsApp/Swiggy… or "Table 5") — this is
 *                what the island appends after the title, e.g. "New Order: Website".
 *   - createdAt: the status-change time, so "x min ago" + recency are accurate.
 *   - filterType: "delivery" | "table" | "dine_in" — used by drawer tab filters.
 */

// Online sales channels (vs. in-house fine-dine/POS). Label shown as the source.
const CHANNEL_LABEL = {
  SWIGGY_ORDER: "Swiggy",
  ZOMATO_ORDER: "Zomato",
  ONDC_ORDER: "ONDC",
  WHATSAPP_ORDER: "WhatsApp",
  B2C_ORDER: "Website",
};

export const DELIVERY_ORDER_TYPES = Object.keys(CHANNEL_LABEL);

const RECENT_WINDOW_MS = 2 * 60 * 60 * 1000; // 2 hours

// Each order surfaces as ONE notification labeled by its current fulfilment
// status, so a brand-new order appears as "New Order" and the same row updates
// as it moves Accepted → Preparing → Ready → Out for delivery → Paid.
const STATUS_NOTIF = {
  CREATED: { type: "NEW_ORDER", title: "New Order" },
  ACCEPTED: { type: "ORDER_ACCEPTED", title: "Order Accepted" },
  PREPARING: { type: "ORDER_PREPARING", title: "Preparing" },
  PACKAGED: { type: "ORDER_READY", title: "Order Ready" },
  IN_TRANSIT: { type: "OUT_FOR_DELIVERY", title: "Out for Delivery" },
  DELIVERED: { type: "PAYMENT_RECEIVED", title: "Payment Received" },
};

// ── Order → notification ──────────────────────────────────────────────────────
export function mapOrderToNotif(order) {
  const base = order?.baseOrderResponse;
  if (!base) return null;

  const status = base?.fulfilmentStatus;
  const meta = STATUS_NOTIF[status];
  if (!meta) return null; // unknown / non-tracked status

  const channel = base?.salesChannel || base?.orderType;
  const tableNo = base?.tableNumber || base?.tableNo || base?.metadata?.tableNumber;
  const amount = base?.totalAmount || base?.grandTotal || base?.orderTotal;
  const orderId = base?.orderId;
  const kotId = base?.kotId || base?.kotNumber;
  // Prefer the status-change time so recency reflects when the event happened.
  const eventAt =
    base?.updatedAt ||
    base?.statusUpdatedAt ||
    base?.modifiedAt ||
    base?.createdAt ||
    base?.orderDate ||
    base?.placedAt;

  const channelLabel = CHANNEL_LABEL[channel]; // Website / WhatsApp / Swiggy / ...
  const isOnline = !!channelLabel;
  // Source shown to the user: online channels → channel name; fine-dine → table no.
  const source = channelLabel || (tableNo ? `Table ${tableNo}` : null);
  const filterType = isOnline ? "delivery" : tableNo ? "table" : "dine_in";

  let { type, title } = meta;
  let description;

  if (status === "CREATED" && isOnline) {
    // Brand-new online/website/whatsapp order.
    type = "NEW_DELIVERY";
    title = "New Order";
    description = `New ${channelLabel} order #${orderId} received - Customer waiting`;
  } else if (status === "CREATED") {
    description = source ? `New order placed for ${source}` : `New order #${orderId} placed`;
  } else if (status === "DELIVERED") {
    description = source
      ? `${source} payment${amount ? ` of ₹${amount}` : ""} received successfully`
      : `Order ${orderId} payment received successfully`;
  } else if (status === "PACKAGED") {
    description = tableNo
      ? `KOT${kotId ? ` #${kotId}` : ""} for Table ${tableNo} is ready to serve`
      : `Order #${orderId} is ready`;
  } else if (status === "IN_TRANSIT") {
    description = `Order #${orderId} is out for delivery`;
  } else {
    // ACCEPTED / PREPARING — generic status line.
    description = source ? `${source} • ${title}` : `Order #${orderId} • ${title}`;
  }

  // First tag is the source (channel for online, table for fine-dine) so it drives
  // the island label, e.g. "New Order: Website" or "Order Ready: Table 5".
  const tags = [source, amount ? `₹${amount}` : null].filter(Boolean);

  return {
    id: `${orderId}_${status}`,
    type,
    title,
    description,
    tags,
    createdAt: eventAt,
    filterType,
  };
}

// Keep undated entries (rather than hide a real one); drop anything older than the
// recency window so stale "1 day ago" orders never appear.
export function isRecent(createdAt, windowMs = RECENT_WINDOW_MS) {
  if (!createdAt) return true;
  const t = new Date(createdAt).getTime();
  if (Number.isNaN(t)) return true;
  return Date.now() - t <= windowMs;
}

// Orders → recent notification list. Both the island and the drawer call this
// (instead of mapping themselves) so they always agree. The getAllOrderlist
// response holds the whole day's orders, so we drop nulls (non-tracked statuses)
// and anything older than the recency window to avoid flooding the UI.
export function buildOrderNotifs(orders = []) {
  return orders
    ?.map(mapOrderToNotif)
    ?.filter(Boolean)
    ?.filter((n) => isRecent(n?.createdAt));
}

// ── Waiter table tasks ────────────────────────────────────────────────────────
export const TASK_TYPE_ICONS = {
  REQUEST_FOR_ASSISTANCE: "🙋",
  REQUEST_FOR_WATER: "💧",
  REQUEST_FOR_BILL: "🧾",
  REQUEST_FOR_ORDER_UPDATES: "📋",
  REQUEST_FOR_PAY_BILL: "💳",
  DEFAULT: "📌",
};

export const TASK_TYPE_COLORS = {
  REQUEST_FOR_ASSISTANCE: "#FF9800",
  REQUEST_FOR_WATER: "#2196F3",
  REQUEST_FOR_BILL: "#4CAF50",
  REQUEST_FOR_ORDER_UPDATES: "#9C27B0",
  REQUEST_FOR_PAY_BILL: "#F44336",
  DEFAULT: "#757575",
};

export const TASK_DESCRIPTIONS = {
  REQUEST_FOR_ASSISTANCE: "Customer needs assistance with menu",
  REQUEST_FOR_WATER: "Water refill requested",
  REQUEST_FOR_BILL: "Bill requested",
  REQUEST_FOR_ORDER_UPDATES: "Customer asking about order status",
  REQUEST_FOR_PAY_BILL: "Customer ready to pay",
};

export function formatTaskType(taskType) {
  return (taskType || "")
    .replace(/REQUEST_FOR_/g, "")
    .replace(/_/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// Flatten the waiter-task API response (list of tables, each with tasks[]) into
// a flat list of normalized notification objects for the dynamic island.
export function mapWaiterTasksToNotifs(waiterTasks = []) {
  const notifs = [];
  (waiterTasks || []).forEach((table) => {
    (table?.tasks || []).forEach((task) => {
      notifs.push({
        id: task?.taskId,
        type: "TABLE_TASK",
        taskAction: task?.taskAction,
        title: formatTaskType(task?.taskAction),
        description: TASK_DESCRIPTIONS[task?.taskAction] || "Task pending",
        tags: [table?.tableName, table?.floorName, table?.areaName].filter(Boolean),
        createdAt:
          task?.requestedDate && task?.requestedAt
            ? `${task?.requestedDate}T${task?.requestedAt}`
            : null,
        filterType: "table_task",
      });
    });
  });
  return notifs;
}

// Most-recent-first sort that tolerates missing/invalid dates.
export function sortByRecent(notifs = []) {
  return [...notifs].sort((a, b) => {
    const ta = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
    return tb - ta;
  });
}
